/**
 * Ledger transport boundary — the chart of accounts.
 *
 * Every request and response alias comes from the generated canonical client;
 * nothing here hand-writes a wire type. The app's HTTP layer owns credentials,
 * idempotency keys and ETag headers.
 *
 * Concurrency policy, verified against openapi/erp.yaml:
 *   - POST /accounts is `client-key` idempotent and MUST carry an
 *     Idempotency-Key.
 *   - PATCH /accounts/{id} takes a REQUIRED strong If-Match and a
 *     merge-patch body: an absent member means "leave it", an explicit null
 *     means "clear it".
 *   - DELETE removes an account that was never used; retirement is
 *     `active: false`, not deletion.
 */
import type { components } from "@erp/api-client";

import {
  apiFetchOperational as apiFetch,
  apiRequestOperational as apiRequest,
  type Page,
} from "@/lib/api/http";
import { collectCursorPages, fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

export type Account = Schemas["Account"];
export type AccountCreate = Schemas["AccountCreate"];
export type AccountPatch = Schemas["AccountPatch"];
export type AccountType = Schemas["AccountType"];
export type NormalBalance = Schemas["NormalBalance"];

export const ACCOUNT_TYPES = [
  "asset",
  "liability",
  "equity",
  "income",
  "expense",
] as const satisfies readonly AccountType[];

export const NORMAL_BALANCES = ["debit", "credit"] as const satisfies readonly NormalBalance[];

/** The normal balance every account of a type carries unless told otherwise —
 *  a contra account (sales returns, accumulated depreciation) is the exception
 *  that has to be chosen deliberately. */
export const DEFAULT_NORMAL_BALANCE: Record<AccountType, NormalBalance> = {
  asset: "debit",
  liability: "credit",
  equity: "credit",
  income: "credit",
  expense: "debit",
};

export interface AccountListQuery {
  account_type?: AccountType;
  parent_id?: string;
  active?: boolean;
  is_postable?: boolean;
  q?: string;
}

/**
 * `ledgerListAccounts` — GET · ledger.account.read.
 *
 * A chart is a tree, and a tree drawn one page at a time is not a tree: a
 * child whose parent sits on the previous page has nothing to indent from.
 * The collection is bounded (thousands at the very most) and the server
 * orders it by materialized path, so every page is drained before drawing.
 */
export function listAccounts(
  query: AccountListQuery = {},
  signal?: AbortSignal,
): Promise<Account[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<Page<Account>>("/accounts", {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

/** `ledgerGetAccount` — GET · ledger.account.read. The ETag is the If-Match
 *  validator a subsequent PATCH requires. */
export function getAccount(accountId: string, signal?: AbortSignal): Promise<WithEtag<Account>> {
  return fetchWithEtag<Account>(`/accounts/${encodeURIComponent(accountId)}`, { signal });
}

/** `ledgerCreateAccount` — POST · ledger.account.create. */
export function createAccount(input: AccountCreate, idempotencyKey: string): Promise<Account> {
  return apiFetch<Account>("/accounts", { method: "POST", body: input, idempotencyKey });
}

/** `ledgerUpdateAccount` — PATCH · ledger.account.update. */
export async function updateAccount(
  accountId: string,
  patch: AccountPatch,
  ifMatch: string,
): Promise<WithEtag<Account>> {
  const { data, etag } = await apiRequest<Account>(`/accounts/${encodeURIComponent(accountId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

/** `ledgerDeleteAccount` — DELETE · ledger.account.delete. */
export function deleteAccount(accountId: string): Promise<void> {
  return apiFetch<void>(`/accounts/${encodeURIComponent(accountId)}`, { method: "DELETE" });
}
